import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Bell, Smartphone, Mail, Volume2, MessageCircle } from "lucide-react";


interface NotificationSettingsProps {
  onBack: () => void;
  onNavigateToChat: () => void;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onCreateNew: () => void;
  user?: {
    email?: string;
    name?: string;
  };
}

export const NotificationSettings = ({ 
  onBack, 
  onNavigateToChat, 
  onNavigateToMyBoyfriends, 
  onNavigateToProfile, 
  onNavigateToSettings,
  onCreateNew,
  user 
}: NotificationSettingsProps) => {
  const [notificationSettings, setNotificationSettings] = useState({
    pushNotifications: true,
    emailNotifications: true,
    soundEffects: true,
    messageNotifications: true,
    newCharacterNotifications: false,
    promotionalEmails: false,
    weeklyDigest: true,
    systemUpdates: true,
    securityAlerts: true,
    vibration: true,
  });

  const handleToggle = (setting: keyof typeof notificationSettings) => {
    setNotificationSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        console.log('Notification permission granted');
      }
    }
  };

  return (
    <div className="bg-gradient-dreamy min-h-screen pb-16">
      {/* Main Content */}
      <div className="p-4 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-primary/20">
            <Button variant="ghost" size="icon" onClick={onNavigateToProfile}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
              Notification Settings
            </h1>
          </div>

          {/* Push Notifications */}
          <Card className="p-6 mb-6 border-primary/20">
            <div className="flex items-center gap-2 mb-4">
              <Smartphone className="h-5 w-5" />
              <h3 className="font-semibold">Push Notifications</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                <div>
                  <h4 className="font-medium">Enable Push Notifications</h4>
                  <p className="text-sm text-muted-foreground">
                    Receive notifications even when the app is closed
                  </p>
                </div>
                <Switch 
                  checked={notificationSettings.pushNotifications}
                  onCheckedChange={() => {
                    handleToggle('pushNotifications');
                    if (!notificationSettings.pushNotifications) {
                      requestNotificationPermission();
                    }
                  }}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                <div>
                  <h4 className="font-medium">New Messages</h4>
                  <p className="text-sm text-muted-foreground">
                    Get notified when your AI boyfriend sends a message
                  </p>
                </div>
                <Switch 
                  checked={notificationSettings.messageNotifications}
                  onCheckedChange={() => handleToggle('messageNotifications')}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                <div>
                  <h4 className="font-medium">Vibration</h4>
                  <p className="text-sm text-muted-foreground">
                    Vibrate on new notifications (mobile only)
                  </p>
                </div>
                <Switch 
                  checked={notificationSettings.vibration}
                  onCheckedChange={() => handleToggle('vibration')}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                <div>
                  <h4 className="font-medium">Promotional Emails</h4>
                  <p className="text-sm text-muted-foreground">
                    Special offers and feature announcements
                  </p>
                </div>
                <Switch 
                  checked={notificationSettings.promotionalEmails}
                  onCheckedChange={() => handleToggle('promotionalEmails')}
                />
              </div>
            </div>
          </Card>

          {/* Email Notifications */}
          <Card className="p-6 mb-6 border-primary/20">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="h-5 w-5" />
              <h3 className="font-semibold">Email Notifications</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                <div>
                  <h4 className="font-medium">Email Notifications</h4>
                  <p className="text-sm text-muted-foreground">
                    Receive important updates via email
                  </p>
                </div>
                <Switch 
                  checked={notificationSettings.emailNotifications}
                  onCheckedChange={() => handleToggle('emailNotifications')}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                <div>
                  <h4 className="font-medium">Weekly Digest</h4>
                  <p className="text-sm text-muted-foreground">
                    Weekly summary of your conversations and activity
                  </p>
                </div>
                <Switch 
                  checked={notificationSettings.weeklyDigest}
                  onCheckedChange={() => handleToggle('weeklyDigest')}
                />
              </div>
            </div>
          </Card>

          {/* System Notifications */}
          <Card className="p-6 mb-6 border-primary/20">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="h-5 w-5" />
              <h3 className="font-semibold">System Notifications</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                <div>
                  <h4 className="font-medium">System Updates</h4>
                  <p className="text-sm text-muted-foreground">
                    Notifications about app updates and new features
                  </p>
                </div>
                <Switch 
                  checked={notificationSettings.systemUpdates}
                  onCheckedChange={() => handleToggle('systemUpdates')}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                <div>
                  <h4 className="font-medium">Security Alerts</h4>
                  <p className="text-sm text-muted-foreground">
                    Important security and account notifications
                  </p>
                </div>
                <Switch 
                  checked={notificationSettings.securityAlerts}
                  onCheckedChange={() => handleToggle('securityAlerts')}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                <div>
                  <h4 className="font-medium">New Character Releases</h4>
                  <p className="text-sm text-muted-foreground">
                    Get notified when new AI personalities are available
                  </p>
                </div>
                <Switch 
                  checked={notificationSettings.newCharacterNotifications}
                  onCheckedChange={() => handleToggle('newCharacterNotifications')}
                />
              </div>
            </div>
          </Card>

          {/* Test Notification */}
          <Card className="p-6 border-primary/20">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">Test Notifications</h4>
                <p className="text-sm text-muted-foreground">
                  Send a test notification to check your settings
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => {
                  if ('Notification' in window && Notification.permission === 'granted') {
                    new Notification('Test Notification', {
                      body: 'Your notification settings are working correctly!',
                      icon: '/favicon.ico'
                    });
                  } else {
                    alert('Test notification sent!');
                  }
                }}
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Send Test
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};